import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formStepActionsStyles as sx } from './FormStepActions.styles';

type Props = {
	onClose: () => void;
	onReview: () => void;
	hasChanges: boolean;
};

export const FormStepActions = ({ onClose, onReview, hasChanges }: Props) => {
	const { t } = useTranslation();
	const { control } = useFormContext();
	const { errors, isValid } = useFormState({ control });

	const addresses = (useWatch({ control, name: 'addresses_V' }) ??
		[]) as unknown[];
	const affiliations = (useWatch({ control, name: 'parentHcos_V' }) ??
		[]) as unknown[];

	const canReview = hasChanges && isValid;
	const hasErrors = Object.keys(errors).length > 0;
	const missingAddressOrAffiliation =
		addresses.length === 0 && affiliations.length === 0;

	const addressesError = errors.addresses_V?.root ?? errors.addresses_V;
	const customError =
		addressesError?.type === 'custom'
			? (addressesError.message as string | undefined)
			: undefined;

	return (
		<Box sx={sx.root}>
			<Box sx={sx.footer}>
				{!canReview && !hasErrors && missingAddressOrAffiliation && (
					<Box sx={sx.errorContainer}>
						<Alert sx={sx.error} severity='warning'>
							{t('Either an Address or an Affiliation is required')}
						</Alert>
					</Box>
				)}

				{!canReview && hasErrors && (
					<Box sx={sx.errorContainer}>
						<Alert sx={sx.error} severity='error'>
							{customError ?? t('Please correct the highlighted fields')}
						</Alert>
					</Box>
				)}

				<Box sx={sx.actions}>
					<Button variant='text' onClick={onClose}>
						{t('Cancel')}
					</Button>
					<Button disabled={!canReview} variant='contained' onClick={onReview}>
						{t('Review')}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
